import { useState } from 'react';
import { Check, Flame, Share2, Trash2, TrendingUp, WifiOff } from 'lucide-react';
import { useLocalCollection } from '@/hooks/useLocalCollection';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { BrandLockup } from '@/components/ui/brand-lockup';
import { BottomSheet } from '@/components/mobile/BottomSheet';
import { HABITS_KEY, bestStreakOf, streakOf, type Habit } from '@/lib/entry';
import { hitFeedback, successFeedback, shareText, tapFeedback } from '@/lib/native';
import { BRAND } from '@/brand/brand';
import { TONES } from '@/brand/tones';

/**
 * The record. Totals worth returning for, and the destructive actions kept safely out of the way of
 * daily use — a delete that sits one tap from a habit you tick every morning will eventually be hit
 * by accident.
 */
export function ProfileScreen() {
	const { items: habits, remove } = useLocalCollection<Habit>(HABITS_KEY);
	const online = useOnlineStatus();
	const [pendingDelete, setPendingDelete] = useState<Habit | null>(null);
	const [shareState, setShareState] = useState<'idle' | 'shared' | 'copied'>('idle');

	const totalDone = habits.reduce((sum, h) => sum + h.done.length, 0);
	const bestEver = habits.reduce((best, h) => Math.max(best, bestStreakOf(h)), 0);
	const activeStreaks = habits.filter((h) => streakOf(h) > 0).length;

	/**
	 * Share through the OS sheet. A real device capability, and the one App Review is happiest to
	 * see — but only via the system sheet, never a row of social icons.
	 */
	const share = async () => {
		void tapFeedback();
		const result = await shareText({
			title: `${BRAND.name} streak`,
			text:
				bestEver > 0
					? `${bestEver} days in a row on ${BRAND.name}. ${totalDone} completions so far.`
					: `I just started tracking habits on ${BRAND.name}.`,
		});
		if (result !== 'unavailable') {
			setShareState(result);
			void successFeedback();
			setTimeout(() => setShareState('idle'), 2200);
		}
	};

	const confirmDelete = () => {
		if (!pendingDelete) return;
		remove(pendingDelete.id);
		void successFeedback();
		setPendingDelete(null);
	};

	return (
		<div className="flex flex-col px-5 pt-6">
			<div className="flex items-center justify-between gap-4">
				<div className="min-w-0">
					<h1 className="text-[28px] font-semibold leading-tight" style={{ fontFamily: 'var(--brand-display)' }}>
						You
					</h1>
					<p className="text-muted-foreground mt-0.5 text-sm">
						{habits.length === 0 ? 'No habits yet' : `${habits.length} habit${habits.length === 1 ? '' : 's'} tracked`}
					</p>
				</div>
				{/* The app's identity, drawn by the kit's lockup rather than an ad-hoc mark. */}
				<BrandLockup name={BRAND.name} size="sm" tone="brand" markOnly />
			</div>

			<Button variant="secondary" size="lg" onClick={share} className="mt-5 min-h-[44px] w-full">
				{shareState === 'idle' ? (
					<>
						<Share2 className="size-4" />
						Share your streak
					</>
				) : (
					<>
						<Check className="size-4" />
						{shareState === 'copied' ? 'Copied to clipboard' : 'Shared'}
					</>
				)}
			</Button>

			<div className="mt-4 grid grid-cols-3 gap-2.5">
				<Stat label="Best streak" value={bestEver} icon={<Flame className="size-4" />} />
				<Stat label="Active" value={activeStreaks} icon={<TrendingUp className="size-4" />} />
				<Stat label="Completed" value={totalDone} />
			</div>

			{habits.length > 0 && (
				<section className="mt-8">
					<h2 className="text-[13px] font-medium uppercase tracking-[0.1em]" style={{ color: 'var(--brand-muted)' }}>
						Your habits
					</h2>
					<div className="mt-3 flex flex-col gap-2">
						{habits.map((habit) => (
							<Card key={habit.id}>
								<CardContent className="flex items-center gap-3 px-4 py-3">
								<span
									className="size-2.5 shrink-0 rounded-full"
									style={{ background: TONES[habit.tone % TONES.length] }}
								/>
								<div className="min-w-0 flex-1">
									<p className="truncate text-[15px] font-medium">{habit.name}</p>
									<p className="text-muted-foreground text-[13px] tabular-nums">
										{habit.done.length} day{habit.done.length === 1 ? '' : 's'} · best {bestStreakOf(habit)}
									</p>
								</div>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => {
											void hitFeedback();
											setPendingDelete(habit);
										}}
										aria-label={`Delete ${habit.name}`}
										className="text-muted-foreground size-11 shrink-0 rounded-full"
									>
										<Trash2 className="size-4" />
									</Button>
								</CardContent>
							</Card>
						))}
					</div>
				</section>
			)}

			<section className="mt-8 mb-4">
				<h2 className="text-[13px] font-medium uppercase tracking-[0.1em]" style={{ color: 'var(--brand-muted)' }}>
					About
				</h2>
				<Card className="mt-3">
					<CardContent className="flex flex-col gap-3 px-4 py-4 text-[14px]">
					<Row label="App" value={BRAND.name} />
					<Separator />
					<Row
						label="Data"
						value="Stored on this device"
					/>
					<Separator />
					<Row
						label="Connection"
						value={
							online ? (
								'Online'
							) : (
								<span className="inline-flex items-center gap-1.5" style={{ color: 'var(--brand-warning)' }}>
									<WifiOff className="size-3.5" />
									Offline — everything still works
								</span>
							)
						}
					/>
					</CardContent>
				</Card>
			</section>

			<BottomSheet
				open={Boolean(pendingDelete)}
				onClose={() => setPendingDelete(null)}
				title="Delete this habit?"
				description={pendingDelete ? `"${pendingDelete.name}" and its ${pendingDelete.done.length}-day history will be removed.` : undefined}
				footer={
					<div className="flex flex-col gap-2.5">
						<Button variant="destructive" size="lg" className="min-h-[48px] w-full rounded-full" onClick={confirmDelete}>
							Delete
						</Button>
						<Button variant="ghost" size="lg" className="min-h-[48px] w-full" onClick={() => setPendingDelete(null)}>
							Keep it
						</Button>
					</div>
				}
			>
				<p className="text-muted-foreground text-sm">This cannot be undone.</p>
			</BottomSheet>
		</div>
	);
}

function Stat({ label, value, icon }: { label: string; value: number; icon?: React.ReactNode }) {
	return (
		<Card>
			<CardContent className="flex flex-col gap-1 px-3 py-3.5">
			<span className="text-muted-foreground flex items-center gap-1">{icon}</span>
			<span className="text-2xl font-semibold tabular-nums leading-none" style={{ fontFamily: 'var(--brand-display)' }}>
				{value}
			</span>
			<span className="text-muted-foreground text-[12px]">{label}</span>
			</CardContent>
		</Card>
	);
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
	return (
		<div className="flex items-baseline justify-between gap-4">
			<span className="text-muted-foreground">{label}</span>
			<span className="text-right">{value}</span>
		</div>
	);
}
